import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, ExternalLink, Info } from 'lucide-react';
import { useAppContext } from '@frontend/context/AppContext';
import { GET_BRAND_CATALOGS_QUERY } from '@frontend/applications/ProductFulfillmentApp/queries/brandCatalog';
import { GET_SELECTION_CRITERIA } from '@frontend/applications/ProductFulfillmentApp/queries/selectionCriteria';
import { useGetCatalogStats } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetCatalogStats';
import { useGetProjectConfigByType } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetProjectConfigByType';
import { ProjectConfigType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { logger } from '@common';
import { AutomationActionNodeSubType, AutomationNodeType } from '@frontend/app/types/globalTypes';
import { GetBrandCatalogsQuery_getBrandCatalogs } from '@frontend/applications/ProductFulfillmentApp/queries/types/GetBrandCatalogsQuery';
import { GetCatalogSelectionRuleByCatalogId_getCatalogSelectionRuleByCatalogId } from '@frontend/applications/ProductFulfillmentApp/queries/types/GetCatalogSelectionRuleByCatalogId';
import { useAuth } from '@frontend/context/authContext';
import { TAutomationActionOption } from '@frontend/app/hooks/automations';
import { MessageTemplate, MessageTemplateInput } from '@frontend/app/hooks';
import { Button } from '@/shadcn/components/ui/button';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Alert, AlertDescription } from '@/shadcn/components/ui/alert';
import { AutomationNodeV2 } from '../../types';
import { SendEmailNotificationForm } from './components/SendEmailNotificationForm';

interface SendProductCatalogActionFormProps {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onSave: () => void;
  projectId?: number;
  actionOptions?: TAutomationActionOption[];
  onUpdateMessageTemplate?: (updatedTemplate: MessageTemplate | MessageTemplateInput) => void;
  resetMessageTemplate?: boolean;
}

export const SendProductCatalogActionForm: React.FC<SendProductCatalogActionFormProps> = ({
  selectedNode,
  onUpdateNode,
  onSave,
  projectId,
  actionOptions = [],
  onUpdateMessageTemplate,
  resetMessageTemplate = false,
}) => {
  const [selectedCatalogId, setSelectedCatalogId] = useState<number | null>(null);
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [emailValidationError, setEmailValidationError] = useState<string>('');
  const [emailValidationErrors, setEmailValidationErrors] = useState<string[]>([]);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);

  // API state management
  const [catalogs, setCatalogs] = useState<GetBrandCatalogsQuery_getBrandCatalogs[]>([]);
  const [criteria, setCriteria] = useState<GetCatalogSelectionRuleByCatalogId_getCatalogSelectionRuleByCatalogId[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState<boolean>(false);
  const [isCriteriaLoading, setIsCriteriaLoading] = useState<boolean>(false);

  const { pfaApolloClient } = useAppContext();
  const { clientInfo } = useAuth();

  // Fetch project-specific catalog configuration
  const { projectConfig, loading: projectConfigLoading } = useGetProjectConfigByType({
    variables: {
      projectId: projectId || 0,
      type: ProjectConfigType.ProductCatalog,
    },
    skip: !projectId,
    client: pfaApolloClient,
  });

  // Fetch catalog statistics when a catalog is selected
  const {
    catalogStats,
    loading: statsLoading,
    error: statsError,
  } = useGetCatalogStats({
    variables: {
      id: selectedCatalogId || 0,
    },
    skip: !selectedCatalogId || Number.isNaN(Number(selectedCatalogId)),
    client: pfaApolloClient, // Use PFA Apollo client
  });

  // Fetch catalogs on component mount
  useEffect(() => {
    if (pfaApolloClient) {
      setCatalogsLoading(true);
      pfaApolloClient
        .query({
          query: GET_BRAND_CATALOGS_QUERY,
        })
        .then((result) => {
          setCatalogs(result.data?.getBrandCatalogs || []);
        })
        .catch((error) => {
          logger.error('Error fetching brand catalogs:', error);
          setCatalogs([]);
        })
        .finally(() => {
          setCatalogsLoading(false);
        });
    }
  }, [pfaApolloClient]);

  // Fetch selection criteria when catalog is selected
  useEffect(() => {
    if (selectedCatalogId && pfaApolloClient) {
      setIsCriteriaLoading(true);
      pfaApolloClient
        .query({
          query: GET_SELECTION_CRITERIA,
          variables: {
            brandCatalogId: selectedCatalogId,
          },
        })
        .then((result) => {
          const fetchedCriteria = result.data?.getCatalogSelectionRuleByCatalogId || [];
          setCriteria(fetchedCriteria);

          // Auto-select single rule if available and no rule is currently selected
          if (fetchedCriteria.length === 1 && selectedRuleId === null) {
            setSelectedRuleId(fetchedCriteria[0].id);
          }
        })
        .catch((error) => {
          logger.error('Error fetching selection criteria:', error);
          setCriteria([]);
        })
        .finally(() => {
          setIsCriteriaLoading(false);
        });
    } else {
      setCriteria([]);
    }
  }, [selectedCatalogId, pfaApolloClient, selectedRuleId]);

  // Initialize state from selectedNode metadata - only when node ID changes
  useEffect(() => {
    if (selectedNode && selectedNode.type === AutomationNodeType.ACTION) {
      // Ensure brandCatalogId is a number
      const brandCatalogId = selectedNode.metadata.brandCatalogId;
      setSelectedCatalogId(brandCatalogId !== null && brandCatalogId !== undefined ? Number(brandCatalogId) : null);

      // Ensure selectionRuleId is a number
      const selectionRuleId = selectedNode.metadata.selectionRuleId;
      setSelectedRuleId(selectionRuleId !== null && selectionRuleId !== undefined ? Number(selectionRuleId) : null);
      setSendEmail(selectedNode.metadata.sendEmail || false);
    }
  }, [selectedNode?.id, selectedNode]);

  // Filter catalogs based on search term and project-specific brandCatalogs
  const filteredCatalogs = useMemo(() => {
    // Get project-specific catalog IDs
    const allowedCatalogIds = projectConfig?.brandCatalogs || [];

    // First filter by project-specific catalogs
    let projectCatalogs = catalogs;
    if (allowedCatalogIds.length > 0) {
      projectCatalogs = catalogs.filter((catalog) => allowedCatalogIds.includes(catalog.id));
    }

    // Then filter by search term
    if (!searchTerm.trim()) return projectCatalogs;
    return projectCatalogs.filter((catalog) => catalog.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, catalogs, projectConfig?.brandCatalogs]);

  // Get selected catalog details
  const selectedCatalog = useMemo(() => {
    return catalogs.find((catalog) => catalog.id === selectedCatalogId) || null;
  }, [selectedCatalogId, catalogs]);

  // Available selection rules from API
  const availableRules = criteria || [];

  // Check if project has no catalogs configured
  const hasNoCatalogsConfigured =
    !projectConfigLoading && (!projectConfig?.brandCatalogs || projectConfig.brandCatalogs.length === 0);

  // Generate create catalog URL
  const createCatalogUrl = useMemo(() => {
    if (!projectId || !clientInfo?.id) return '#';

    return `/client/${clientInfo.id}/settings/brandCatalogs/new`;
  }, [projectId, clientInfo?.id]);

  const handleCatalogSelect = (catalogId: string) => {
    const numCatalogId = parseInt(catalogId, 10);
    setSelectedCatalogId(numCatalogId);
    setSelectedRuleId(null); // Reset rule selection when catalog changes
    setSearchTerm(''); // Clear search term when changing catalogs

    // Don't update node metadata immediately - wait for save
  };

  const handleRuleSelect = (ruleId: string) => {
    const numRuleId = parseInt(ruleId, 10);
    setSelectedRuleId(numRuleId);

    // Don't update node metadata immediately - wait for save
  };

  const validateSelection = () => {
    let error = '';
    let emailError = '';

    if (hasNoCatalogsConfigured) {
      error = 'No catalogs are configured for this project. Please create a catalog first.';
    } else if (!selectedCatalogId) {
      error = 'Please select a catalog';
    } else if (availableRules.length > 0 && selectedRuleId === null) {
      error = 'Please select a rule';
    }

    setValidationError(error);

    // Check email validation if email is enabled
    if (sendEmail && !isEmailValid) {
      if (emailValidationErrors.length > 0) {
        emailError = emailValidationErrors[0];
      } else {
        emailError = 'Please complete email configuration';
      }
      setEmailValidationError(emailError);
    } else {
      setEmailValidationError('');
    }

    return error === '' && emailError === '';
  };

  // Helper function to check if current selections are valid (without setting error)
  const isCurrentSelectionValid = useCallback(() => {
    if (hasNoCatalogsConfigured) return false;
    if (!selectedCatalogId) return false;
    if (availableRules.length > 0 && selectedRuleId === null) return false;
    return true;
  }, [hasNoCatalogsConfigured, selectedCatalogId, availableRules.length, selectedRuleId]);

  // Clear validation errors when selections become valid
  useEffect(() => {
    // Only clear errors if there's currently an error and the selection is now valid
    if (validationError && isCurrentSelectionValid()) {
      setValidationError('');
    }
  }, [
    selectedCatalogId,
    selectedRuleId,
    hasNoCatalogsConfigured,
    availableRules.length,
    validationError,
    isCurrentSelectionValid,
  ]);

  const handleSave = () => {
    if (!validateSelection()) return;

    // Prepare metadata object
    const metadata = {
      ...selectedNode.metadata,
      brandCatalogId: selectedCatalogId !== null ? Number(selectedCatalogId) : selectedCatalogId,
      sendEmail,
    };

    // Only add selectionRuleId if it exists and is not null
    if (selectedRuleId !== null && selectedRuleId !== undefined) {
      metadata.selectionRuleId = Number(selectedRuleId);
    } else {
      // Explicitly remove selectionRuleId if no rules are available or selected
      delete metadata.selectionRuleId;
    }

    // Update the node metadata with the form values
    onUpdateNode(selectedNode.id, {
      metadata,
    });

    onSave();
  };

  const updateSendEmailState = (value: boolean) => {
    setSendEmail(value);
    // Update node metadata immediately but preserve catalog selection
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        sendEmail: value,
        // Preserve current local state for catalog selection
        brandCatalogId: selectedCatalogId !== null ? Number(selectedCatalogId) : selectedNode.metadata.brandCatalogId,
        selectionRuleId: selectedRuleId !== null ? Number(selectedRuleId) : selectedNode.metadata.selectionRuleId,
      },
    });
  };

  const handleEmailMetadataUpdate = (updates: any) => {
    // Handle template and resource metadata updates from SendEmailNotificationForm
    // Preserve current local state for catalog selection
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        ...updates,
        // Preserve current local state for catalog selection
        brandCatalogId: selectedCatalogId !== null ? Number(selectedCatalogId) : selectedNode.metadata.brandCatalogId,
        selectionRuleId: selectedRuleId !== null ? Number(selectedRuleId) : selectedNode.metadata.selectionRuleId,
      },
    });
  };

  const handleEmailValidationChange = useCallback((isValid: boolean, errors: string[]) => {
    setIsEmailValid(isValid);
    setEmailValidationErrors(errors);
    // Clear email error when validation passes
    if (isValid) {
      setEmailValidationError('');
    }
  }, []);

  // Combine SEND_PRODUCT_CATALOG and SEND_EMAIL templates and resources for email form
  const combinedActionOptions = useMemo(() => {
    const sendProductCatalogOption = actionOptions.find(
      (option) => option.type === AutomationActionNodeSubType.SEND_PRODUCT_CATALOG,
    );
    const sendEmailOption = actionOptions.find((option) => option.type === 'SEND_EMAIL');

    if (!sendProductCatalogOption && !sendEmailOption) return [];

    // Create a combined option with resources from SEND_PRODUCT_CATALOG and templates from both
    const combinedOption = {
      ...(sendProductCatalogOption || {}),
      type: AutomationActionNodeSubType.SEND_PRODUCT_CATALOG,
      emailResources: sendProductCatalogOption?.emailResources || [],
      messageTemplates: [
        ...(sendProductCatalogOption?.messageTemplates || []),
        ...(sendEmailOption?.messageTemplates || []),
      ],
    } as any;

    return [combinedOption];
  }, [actionOptions]);

  return (
    <div className="h-full flex flex-col overflow-hidden p-1">
      {/* Scrollable content area with bottom padding for buttons */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pb-40">
        {/* Catalog selection section */}
        <div className="space-y-3">
          <div className="relative self-stretch mt-[-1.00px] font-medium text-grey-6 text-sm">Select Catalog</div>

          <div className="flex flex-col items-start gap-4 relative self-stretch w-full flex-[0_0_auto]">
            <div className="space-y-2 w-full">
              <Select
                value={selectedCatalogId ? String(selectedCatalogId) : ''}
                onValueChange={handleCatalogSelect}
                disabled={catalogsLoading || projectConfigLoading || hasNoCatalogsConfigured}
              >
                <SelectTrigger className="flex items-center gap-1 px-3 py-[5px] relative self-stretch w-full flex-[0_0_auto] bg-gray-0 rounded-lg border border-solid border-gray-2">
                  <SelectValue
                    className="relative flex-1 mt-[-1.00px] font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-gray-6 text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)]"
                    placeholder={
                      hasNoCatalogsConfigured
                        ? 'No catalogs available'
                        : catalogsLoading || projectConfigLoading
                          ? 'Loading catalogs...'
                          : 'Select Catalog'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {/* Search input - only show if there are catalogs */}
                  {!hasNoCatalogsConfigured && (
                    <div className="flex items-center gap-2 px-2 py-1 border-b">
                      <Search className="w-4 h-4 text-grey-4" />
                      <Input
                        placeholder="Search Catalog"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-0 p-0 h-6 focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </div>
                  )}
                  {/* Catalog options */}
                  {catalogsLoading || projectConfigLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading catalogs...
                    </SelectItem>
                  ) : hasNoCatalogsConfigured ? (
                    <SelectItem value="no-catalogs" disabled>
                      No catalogs available
                    </SelectItem>
                  ) : filteredCatalogs.length === 0 ? (
                    <SelectItem value="no-filtered-catalogs" disabled>
                      No catalogs match your search
                    </SelectItem>
                  ) : (
                    filteredCatalogs.map((catalog) => (
                      <SelectItem key={catalog.id} value={String(catalog.id)}>
                        {catalog.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Empty state message when no catalogs are configured */}
            {hasNoCatalogsConfigured && (
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  No catalogs are currently configured for this project.{' '}
                  <a
                    href={createCatalogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-blue-900"
                  >
                    You can create a catalog using the link here.
                  </a>
                </AlertDescription>
              </Alert>
            )}

            {/* Show catalog statistics when catalog is selected */}
            {selectedCatalog && (
              <div className="flex flex-col items-start gap-2 relative self-stretch w-full flex-[0_0_auto]">
                <div className="flex flex-col items-start gap-3 p-3 relative self-stretch w-full flex-[0_0_auto] bg-grey-1 rounded-2xl">
                  <div className="items-center gap-3 self-stretch w-full flex-[0_0_auto] flex relative">
                    <div className="items-start gap-4 flex-1 grow flex relative">
                      {statsLoading ? (
                        // Show loading state
                        <div className="text-sm text-grey-5">Loading statistics...</div>
                      ) : statsError ? (
                        // Show error state
                        <div className="text-sm text-red-500">Error loading statistics</div>
                      ) : (
                        // Show real data from API
                        <>
                          <div className="flex flex-col items-start justify-center gap-1 relative flex-1 grow">
                            <div className="relative w-fit mt-[-1.00px] font-semibold-16px-24px font-[number:var(--semibold-16px-24px-font-weight)] text-grey-6 text-[length:var(--semibold-16px-24px-font-size)] tracking-[var(--semibold-16px-24px-letter-spacing)] leading-[var(--semibold-16px-24px-line-height)] whitespace-nowrap [font-style:var(--semibold-16px-24px-font-style)]">
                              {catalogStats?.collectionCount || 0}
                            </div>
                            <div className="relative w-fit font-regular-75-caption font-[number:var(--regular-75-caption-font-weight)] text-grey-5 text-[length:var(--regular-75-caption-font-size)] tracking-[var(--regular-75-caption-letter-spacing)] leading-[var(--regular-75-caption-line-height)] whitespace-nowrap [font-style:var(--regular-75-caption-font-style)]">
                              Collections
                            </div>
                          </div>
                          <div className="flex flex-col items-start justify-center gap-1 relative flex-1 grow">
                            <div className="relative w-fit mt-[-1.00px] font-semibold-16px-24px font-[number:var(--semibold-16px-24px-font-weight)] text-grey-6 text-[length:var(--semibold-16px-24px-font-size)] tracking-[var(--semibold-16px-24px-letter-spacing)] leading-[var(--semibold-16px-24px-line-height)] whitespace-nowrap [font-style:var(--semibold-16px-24px-font-style)]">
                              {catalogStats?.productCount || 0}
                            </div>
                            <div className="relative w-fit font-regular-75-caption font-[number:var(--regular-75-caption-font-weight)] text-grey-5 text-[length:var(--regular-75-caption-font-size)] tracking-[var(--regular-75-caption-letter-spacing)] leading-[var(--regular-75-caption-line-height)] whitespace-nowrap [font-style:var(--regular-75-caption-font-style)]">
                              Products
                            </div>
                          </div>
                          <div className="flex flex-col items-start justify-center gap-1 relative flex-1 grow">
                            <div className="relative w-fit mt-[-1.00px] font-semibold-16px-24px font-[number:var(--semibold-16px-24px-font-weight)] text-grey-6 text-[length:var(--semibold-16px-24px-font-size)] tracking-[var(--semibold-16px-24px-letter-spacing)] leading-[var(--semibold-16px-24px-line-height)] whitespace-nowrap [font-style:var(--semibold-16px-24px-font-style)]">
                              {catalogStats?.variantCount || 0}
                            </div>
                            <div className="relative w-fit font-regular-75-caption font-[number:var(--regular-75-caption-font-weight)] text-grey-5 text-[length:var(--regular-75-caption-font-size)] tracking-[var(--regular-75-caption-letter-spacing)] leading-[var(--regular-75-caption-line-height)] whitespace-nowrap [font-style:var(--regular-75-caption-font-style)]">
                              Variants
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      className="flex flex-col w-6 h-6 items-center justify-center gap-2 relative bg-grey-1 rounded-lg border border-solid border-grey-5"
                    >
                      <ExternalLink className="w-3 h-3 text-grey-9" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {validationError && <p className="text-sm text-red-500">{validationError}</p>}
        </div>

        {/* Selection Rule section */}
        {selectedCatalog && (
          <div className="flex flex-col items-start relative bg-gray-0 rounded-xl">
            <div className="flex items-center gap-1 pt-0 pb-2 px-0 relative self-stretch w-full flex-[0_0_auto]">
              <Label className="relative w-fit mt-[-1.00px] font-medium-75-labels font-[number:var(--medium-75-labels-font-weight)] text-gray-9 text-[length:var(--medium-75-labels-font-size)] tracking-[var(--medium-75-labels-letter-spacing)] leading-[var(--medium-75-labels-line-height)] whitespace-nowrap [font-style:var(--medium-75-labels-font-style)]">
                Selection Rule
              </Label>
            </div>
            <Select
              value={selectedRuleId !== null && selectedRuleId !== undefined ? String(selectedRuleId) : ''}
              onValueChange={handleRuleSelect}
              disabled={!selectedCatalogId || isCriteriaLoading || availableRules.length === 0}
            >
              <SelectTrigger className="flex items-center gap-1 px-3 py-[5px] relative self-stretch w-full flex-[0_0_auto] bg-gray-0 rounded-lg border border-solid border-gray-2">
                <SelectValue
                  className="relative flex-1 mt-[-1.00px] font-regular-100-paragraph font-[number:var(--regular-100-paragraph-font-weight)] text-gray-6 text-[length:var(--regular-100-paragraph-font-size)] tracking-[var(--regular-100-paragraph-letter-spacing)] leading-[var(--regular-100-paragraph-line-height)] [font-style:var(--regular-100-paragraph-font-style)]"
                  placeholder={
                    !selectedCatalogId
                      ? 'Select a catalog first'
                      : isCriteriaLoading
                        ? 'Loading selection rules...'
                        : availableRules.length === 0
                          ? 'No selection rules available'
                          : availableRules.length === 1
                            ? `${availableRules[0].label || `Rule ${availableRules[0].id}`} (auto-selected)`
                            : 'Select a rule'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {/* Only show rules if there are any available - no "No Selection Rule" option when rules exist */}
                {!isCriteriaLoading &&
                  availableRules.map((rule) => (
                    <SelectItem key={rule.id} value={String(rule.id)}>
                      {rule.label || `Rule ${rule.id}`}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Email notification section */}
        <div className="relative">
          <SendEmailNotificationForm
            selectedNode={selectedNode}
            actionOptions={combinedActionOptions}
            onUpdateMessageTemplate={onUpdateMessageTemplate}
            resetMessageTemplate={resetMessageTemplate}
            sendEmail={sendEmail}
            setSendEmail={updateSendEmailState}
            onEmailMetadataUpdate={handleEmailMetadataUpdate}
            onValidationChange={handleEmailValidationChange}
          />
          {emailValidationError && sendEmail && <p className="text-sm text-red-500 mt-2">{emailValidationError}</p>}
        </div>
      </div>

      {/* Action buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-start p-4 border-t bg-white shadow-lg z-50">
        <Button variant="outline" onClick={handleSave} className="bg-white">
          Done
        </Button>
      </div>
    </div>
  );
};
